import PropTypes from 'prop-types';
import { useState } from 'react';

import { Box } from '@hivebrite/design-system/Box';
import { ButtonInLink } from '@hivebrite/design-system/ButtonInLink';
import { Card } from '@hivebrite/design-system/Card';
import { Skeleton } from '@hivebrite/design-system/Skeleton';

import {
  ACCEPTED,
  END_RELATIONSHIP,
  INACTIVE,
  PENDING,
} from 'hb-react/frontoffice/apps/Mentorships/constants';
import { useMentorshipsContext } from 'hb-react/frontoffice/apps/Mentorships/contexts/MentorshipsContext';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { useShouldDisplayInternalMessagingAction } from 'hb-react/frontoffice/utils/messaging';
import ModalWithTemplate from 'hb-react/shared/components/DesignSystem/Modal/ModalWithTemplate';

import { actionItemsIcons as icons } from '../actionItemsIcons';
import TerminationForm from '../Forms/TerminationForm';
import MentorshipCardBody from '../MentorshipCardBody';
import ApplyToBeMenteeModal from '../Modals/ApplyToBeMenteeModal';

const MentorCard = ({
  onEndRelationship,
  customAttributesList,
  mentor,
  reachedMaxMentees,
  status,
  hideDisabledSubmitButtons,
}) => {
  const [modalType, setModalType] = useState(null);

  const dropDownItems = [
    {
      dataTestId: 'mentorship-finish-relation',
      icon: icons.ban,
      label: s__('Mentorships|End relationship'),
      onClick: () => setModalType(END_RELATIONSHIP),
    },
  ];

  const handleTerminateMentor = (message) => {
    if (onEndRelationship) {
      onEndRelationship(message);
    }
  };

  return (
    <Card height="100%" pb={5} pl={4} pr={4} pt={4}>
      <Box flexDirection="column" height="100%">
        <MentorshipCardBody
          customAttributesList={customAttributesList}
          dropdownItems={status === ACCEPTED ? dropDownItems : []}
          mentorProfile={mentor}
          status={status}
        />
        <MentorButtons
          mentor={mentor}
          reachedMaxMentees={reachedMaxMentees}
          status={status}
          hideDisabledSubmitButtons={hideDisabledSubmitButtons}
        />
        {modalType && (
          <ModalWithTemplate
            onCloseModal={() => setModalType(null)}
            header={s__('Mentorships|End mentoring relationship')}
          >
            {modalType === END_RELATIONSHIP && (
              <Box width="100%">
                <TerminationForm
                  validations={{ required: true, maxLength: 1000 }}
                  label={s__('Mentorships|End relationship')}
                  placeholder={s__(
                    'Mentorships|Please provide a note as to why you are ending the relationship. Typical messages include: saying thank you, what you learned, and highlighing strengths or areas to improve in the future. Thank you.',
                  )}
                  onSubmit={handleTerminateMentor}
                />
              </Box>
            )}
          </ModalWithTemplate>
        )}
      </Box>
    </Card>
  );
};

const MentorButtons = ({
  mentor,
  reachedMaxMentees,
  status,
  hideDisabledSubmitButtons,
}) => {
  const { currentUser } = useDefaultAppContexts();
  const { mentorLabel, menteeLabel } = useMentorshipsContext();
  const [isPending, setIsPending] = useState(status === PENDING);

  const displayMessagingButton = useShouldDisplayInternalMessagingAction({
    user: mentor.user,
  });

  let disabledApplyButtonMessage = null;

  if (isPending) {
    disabledApplyButtonMessage = s__(
      'You have already submitted a request to this %{mentorLabel}',
    ).i__({
      mentorLabel: mentorLabel.toLowerCase(),
    });
  } else if (reachedMaxMentees) {
    disabledApplyButtonMessage = s__(
      'Mentorships|This %{mentorLabel} has reached their limit of %{menteeLabel}',
    ).i__({
      mentorLabel: mentorLabel.toLowerCase(),
      menteeLabel: menteeLabel.toLowerCase(),
    });
  }

  let showApplyButton =
    (status === INACTIVE && currentUser?.can_be_a_mentee) || isPending;

  if (!!disabledApplyButtonMessage && hideDisabledSubmitButtons) {
    showApplyButton = false;
  }
  const applyButton = showApplyButton && (
    <>
      <ApplyToBeMenteeModal
        mentor={mentor}
        disabled={!!disabledApplyButtonMessage}
        setIsPending={setIsPending}
      />
      {disabledApplyButtonMessage && (
        <Box mt={2} font="footnote-asphalt-italic" fontSize={0}>
          {disabledApplyButtonMessage}
        </Box>
      )}
    </>
  );

  const sendMessageButton = status === ACCEPTED && displayMessagingButton && (
    <ButtonInLink
      linkProps={{
        to: Routes.messaging_new_conversation_path({
          recipient_ids: mentor.user.id,
        }),
      }}
    >
      {s__('Mentorships|Send message')}
    </ButtonInLink>
  );

  if (applyButton || sendMessageButton) {
    return (
      <Box flexDirection="column" alignItems="flex-start" pt={4}>
        {applyButton}
        {sendMessageButton}
      </Box>
    );
  }

  return null;
};

export const MentorCardLoader = () => (
  <Card height={{ _: 200 }} p={4}>
    <Box mb={4}>
      <Skeleton height={60} width={60} mr={3} css="border-radius: 50%;" />
      <Box display="block" width="100%" mt="10px" css="flex: 1 1 0%;">
        <Skeleton height={20} mb={1} width="100%" />
        <Skeleton height={17} width="60%" />
      </Box>
    </Box>

    <Skeleton height={15} mb={2} width="100%" />
    <Skeleton height={15} mb={2} width="70%" />
    <Skeleton height={15} mb={2} width="40%" />
    <Skeleton height={15} width="60%" />
  </Card>
);

MentorCard.propTypes = {
  onEndRelationship: PropTypes.func,
  mentor: PropTypes.object.isRequired,
  status: PropTypes.oneOf([
    'inactive',
    'accepted',
    'rejected',
    'on_pause',
    'pending',
    'terminated',
  ]).isRequired,
  reachedMaxMentees: PropTypes.bool,
};

export default MentorCard;
